'use client';

import {CodeEditor} from 'common/components/code_editor';
import {NonCodingEditor} from 'common/components/non_coding_editor';
import {FRONTEND_URL} from 'config';

import {SubmitExperienceModal} from 'iq-interview_experiences/components/submit_experience_modal';
import type {InterviewGuideTabProps} from 'iq-interview_guides/types';
import {LevelPillTag} from 'iq-questions/components/level_pilltag';
import {QuestionsTable} from 'iq-questions/components/questions_table';
import {TopicDistributionCard} from 'iq-questions/components/topic_distribution_card';
import {useEffect, useState} from 'react';

export const QuestionsTab: React.FC<InterviewGuideTabProps> = ({
  interviewGuide,
  isActive,
}: InterviewGuideTabProps) => {
  const [cardVisible, setCardVisible] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    if (isActive) {
      const id = requestAnimationFrame(() => setCardVisible(true));
      return () => cancelAnimationFrame(id);
    } else {
      setCardVisible(false);
    }
  }, [isActive]);

  const isCodingQuestion = (interviewGuide.featured_question.languages?.length ?? 0) > 0;

  return (
    <div className='w-full '>
      {/* {coderBeyondLimit && <CoderUpsellBanner onShare={onShare} />} */}

      <h2 className='text-xl font-bold text-iq-black mb-1'>
        Questions asked at {interviewGuide.company_name} {interviewGuide.position_name} interview
      </h2>
      <p className='text-sm text-iq-grey-medium mb-6'>
        Sourced from candidate reports and verified by our team.
      </p>
      <div
        className={`mb-4 transition-opacity duration-500 ${cardVisible ? 'opacity-100' : 'opacity-0'}`}>
        <TopicDistributionCard topicWeights={interviewGuide.data.topic_weights} />
      </div>

      {/* Featured question */}
      <div className='bg-white border-2 border-iq-orange-light rounded-xl p-6 mb-8'>
        <p className='text-xs font-bold text-iq-orange uppercase tracking-wide mb-3'>
          Featured question at {interviewGuide.company_name}
        </p>
        <h3 className='text-base font-bold text-iq-black mb-2'>
          {interviewGuide.featured_question.title}
        </h3>
        <div className='flex items-center gap-2 mb-3'>
          <span className='text-xs px-2.5 py-1 bg-iq-grey-light text-iq-grey-dark rounded-full'>
            {interviewGuide.featured_question.topic.name}
          </span>
          <LevelPillTag level={interviewGuide.featured_question.level} />
        </div>
        <p className='text-sm text-iq-grey-dark mb-4'>{interviewGuide.featured_question.summary}</p>

        {/* Editor — CodeEditor for coding questions, LlmEditor for non-coding */}
        <div className='mb-5'>
          {isCodingQuestion ? (
            <CodeEditor
              data={interviewGuide.featured_question}
              viewUrl={`${FRONTEND_URL}/questions/${interviewGuide.featured_question.slug}`}
            />
          ) : (
            <NonCodingEditor
              data={interviewGuide.featured_question}
              viewUrl={`${FRONTEND_URL}/questions/${interviewGuide.featured_question.slug}`}
            />
          )}
        </div>
      </div>

      {/* Questions table */}
      <h3 className='text-base font-bold text-iq-black mb-3'>Selected questions</h3>
      <QuestionsTable questions={interviewGuide.questions} onShare={() => setShareOpen(true)} />

      {shareOpen && (
        <SubmitExperienceModal
          guideSlug={interviewGuide.slug}
          defaultCompanySlug={interviewGuide.company_slug}
          defaultPositionSlug={interviewGuide.position_slug}
          companyId={interviewGuide.company_id}
          positionId={interviewGuide.position_id}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
};
